import React from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

import { IMAGE_API_URL } from 'Constants';
import {
	decksForClass,
	SeatingTabClass,
	selectableSeatsForClass,
	SeatingTabSeat,
	seatingIsEmpty,
} from 'Services/Api/_HumanWritten/SeatingService';
import Spinner from 'Views/Components/Spinner/Spinner';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { getPassengerTickets, setPassengerTickets } from '../../BookingWizardData/BaseBookingWizardData';
import { PassengerDetail, passengerClassPatch } from '../../BookingWizardData/PassengerDetail';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { appConfigStore } from 'Models/AppConfigStore';
import { PassengerField, PassengerTypeKey, wizardModeOptions } from 'Models/Enums';
import { useSeatingTabLegs } from './useSeatingTab';
import { useSeatingStore } from 'Stores/SeatingStore';
import useStore from 'Hooks/useStore';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import SeatingDeck from './SeatingDeck';
import FareTypeOption from './FareTypeOption';
import { deckSeatLabel, describeClassPoints } from './seatingLayout';
import { classColour } from 'Modules/Ferries/Utils/seatGrid';
import { ClassMoreInfo } from './ClassMoreInfo';
import { parseDeckFeatures } from 'Modules/Ferries/Utils/deckFeatures';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { unseatedFreeSeatPassengers } from './seatingTabGating';

/**
 * Money on this step, in the same form as every other price in the wizard.
 *
 * <p>Every amount here was interpolated raw, so a $3.00 seat fee printed as `$3` and a $20.50 fare
 * as `$20.5`. The cart and the sidebar next to them are formatted, so the same charge read two
 * different ways on one screen.</p>
 */
const money = (amount: number) => formatPriceDisplay(amount, false);

/**
 * Where the booker picks a fare class, and a seat within it.
 *
 * <p>Classes and seats are independent. A class with no seats is unreserved seating with a headcount
 * cap and a price; a seat with no classes is pickable by anyone. The step renders whichever halves
 * the trip's ferry actually has, and the wizard excludes it entirely when it has neither.</p>
 *
 * <p>The class is chosen per passenger from a list rather than a dropdown, and the seat from the
 * deck rather than a second dropdown — a menu that opens inside a scrolling step is the thing that
 * put the last passenger's class out of reach.</p>
 */
export function SeatingTab() {
	const { wizardData, setWizardData } = useBookingWizardDataStore();
	const store = useStore();
	const legs = useSeatingTabLegs();

	/**
	 * Which sailing is being seated.
	 *
	 * <p>A passenger holds a seat on each leg they travel, and the two legs are different trips on
	 * possibly different vessels — different decks, different classes, different seats already gone.
	 * So the step is switched between them rather than showing one and silently applying it to
	 * both.</p>
	 */
	const leg = useSeatingStore(x => x.leg);
	const setLeg = useSeatingStore(x => x.setLeg);
	const seatWarningFor = useSeatingStore(x => x.seatWarningFor);
	const setSeatWarningFor = useSeatingStore(x => x.setSeatWarningFor);
	// Back to the outbound leg whenever the step is left, so re-entering it starts where it always
	// did. The store only exists so the wizard's `Continue` can reach the leg while the step is up.
	// The seat warning goes too: coming back to the step is a fresh look, and is warned afresh.
	React.useEffect(
		() => () => {
			setLeg('departing');
			setSeatWarningFor(null);
		},
		[setLeg, setSeatWarningFor],
	);
	const seatWarningRef = React.useRef<HTMLDivElement>(null);
	// `Continue` raised it, and the button is at the foot of the screen while the passenger it is
	// about may be well above — so it is brought into view rather than left to be found.
	React.useEffect(() => {
		if (seatWarningFor && seatWarningRef.current) {
			scrollBelowProgressBar(seatWarningRef.current);
		}
	}, [seatWarningFor]);
	const hasReturn = !!wizardData?.returningJourney?.tripId;
	// A leg whose vessel sells no fare types and has no seats has nothing to choose, so it gets no
	// tab and the step opens on the leg that does. Still loading counts as having something, so the
	// step does not jump legs while the data arrives.
	const departingHasSeating = legs.departing.isLoading || !seatingIsEmpty(legs.departing.data);
	const returningHasSeating = hasReturn && (legs.returning.isLoading || !seatingIsEmpty(legs.returning.data));
	const showLegTabs = departingHasSeating && returningHasSeating;
	const activeLeg = returningHasSeating && (leg === 'returning' || !departingHasSeating) ? 'returning' : 'departing';
	const isReturning = activeLeg === 'returning';

	const { data, isLoading } = isReturning ? legs.returning : legs.departing;

	/**
	 * The fare type this passenger is in <b>on this leg</b>.
	 *
	 * <p>One per leg for the same reason as the seat: the two sailings are sold separately, stored
	 * separately and priced separately. Reading one field for both made choosing on the return leg
	 * rewrite the departing one.</p>
	 */
	const classIdOf = React.useCallback(
		(passenger: PassengerDetail) =>
			isReturning ? passenger.returningPassengerClassId : passenger.passengerClassId,
		[isReturning],
	);
	const classPatch = React.useCallback(
		(passengerClassId: string | undefined): Partial<PassengerDetail> =>
			passengerClassPatch(
				isReturning ? 'return' : 'departure',
				passengerClassId,
				data?.passengerClasses.find(x => x.id === passengerClassId)?.name,
			),
		[isReturning, data],
	);

	/** The field this leg's seat lives in. One passenger, one seat per sailing. */
	const seatIdOf = React.useCallback(
		(passenger: PassengerDetail) => (isReturning ? passenger.returningSeatId : passenger.seatId),
		[isReturning],
	);
	const seatLabelOf = (passenger: PassengerDetail) =>
		isReturning ? passenger.returningSeatLabel : passenger.seatLabel;
	/**
	 * A seat the booker chose and has paid for. Moving it costs nothing more, so the paid-seat offer
	 * stays out of their way; a seat the system handed out was not chosen, and replacing it with a
	 * chosen one is still charged.
	 */
	const holdsChosenSeat = (passenger: PassengerDetail) =>
		!!seatIdOf(passenger) && !(isReturning ? passenger.returningSeatAutoAssigned : passenger.seatAutoAssigned);
	/**
	 * @param autoAssigned true where the system chose the seat, false where the booker did.
	 *
	 * <p>Carried because it decides the money: the selection fee is charged for a choice, and the
	 * seats everyone arrives with were not chosen by anyone.</p>
	 */
	const seatPatch = React.useCallback(
		(seatId: string | undefined, seatLabel: string | undefined, autoAssigned = false): Partial<PassengerDetail> =>
			isReturning
				? {
						returningSeatId: seatId,
						returningSeatLabel: seatLabel,
						returningSeatAutoAssigned: autoAssigned,
					}
				: { seatId, seatLabel, seatAutoAssigned: autoAssigned },
		[isReturning],
	);

	const passengers = React.useMemo(() => collectPassengers(wizardData), [wizardData]);

	/**
	 * The passenger being dealt with — their class and their seat, in one place.
	 *
	 * <p>One id, not two. The step used to hold an "expanded" passenger for the class list and a
	 * separate "seating" passenger for the diagram below it, so the card you had open and the person
	 * the deck was assigning to could be two different people — and the notice about seat fees
	 * belonged to neither of them.</p>
	 */
	const [activePassengerId, setActivePassengerId] = React.useState<string | null>(null);
	/**
	 * Whether the step is still choosing who to open for the booker, or has been told.
	 *
	 * <p>Needed because "nobody is open" and "nobody has been picked yet" are different states that
	 * a single nullable id cannot tell apart. Without it, Done set the id to null, the fallback chain
	 * read that as "pick someone" and opened the same card again — which on a booking with one
	 * passenger made the button look dead.</p>
	 */
	const [autoOpen, setAutoOpen] = React.useState(true);
	/** Which deck's plan is on screen. Follows the passenger's class unless the booker overrides it. */
	const [openDeckId, setOpenDeckId] = React.useState<string | null>(null);
	/*
		What was open belonged to the leg that was showing, so changing leg clears it.

		Here rather than in the tab strip's own handler because the leg can now change from outside
		this component — the wizard's `Continue` moves it — and a deck left open from the outbound
		sailing is a plan of a vessel the booker is no longer looking at.
	*/
	React.useEffect(() => {
		setActivePassengerId(null);
		setOpenDeckId(null);
	}, [leg]);
	/**
	 * The passenger whose plan the booker has opened, where their fare type charges for a seat.
	 *
	 * <p>A paid seat starts shut. Every passenger is seated free at check-in, so the plan is an offer
	 * to spend money rather than a step to complete — opening it by default asks a booker who had no
	 * intention of paying to scroll past two hundred seats to reach the next person. A free fare type
	 * has nothing to weigh up and stays open.</p>
	 */
	const [payingSeatOpenFor, setPayingSeatOpenFor] = React.useState<string | null>(null);
	/**
	 * The passenger whose full list of fare types is open, or null.
	 *
	 * <p>A passenger who already holds a fare type sees only that one, with `Change fare type` to
	 * bring the rest back. Four fare types with descriptions and prices stood between the name and the
	 * seat plan on every card, so the plan — the thing left to do — started below the screen.</p>
	 */
	const [fareListOpenFor, setFareListOpenFor] = React.useState<string | null>(null);
	/**
	 * The passenger a manager is seating outside their fare type, or null.
	 *
	 * <p>Offered only when the passenger's own fare type has no seat left — an overbooked fare type —
	 * and only to managers, who are the ones able to overbook one. The passenger keeps their fare type
	 * and its fare; the seat is free. See <c>Permission.SeatOutsideFareType</c> on the server.</p>
	 */
	const [otherFareTypesFor, setOtherFareTypesFor] = React.useState<string | null>(null);
	/** Which class has its "more info" open. One at a time — two open panels is a wall of text. */
	const [openInfoClassId, setOpenInfoClassId] = React.useState<string | null>(null);
	/**
	 * The passenger card to bring into view once it has rendered, or null for none.
	 *
	 * <p>Set when a seat is picked. The plan is tall, so by the time the booker has found a seat the
	 * next passenger's card starts well below the fold — it opened, but nothing on screen said so.
	 * Held as state rather than scrolled on the spot because the card only takes its open shape on
	 * the next render.</p>
	 */
	const [scrollToPassengerId, setScrollToPassengerId] = React.useState<string | null>(null);
	const cardRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
	React.useEffect(() => {
		if (!scrollToPassengerId) {
			return;
		}
		const card = cardRefs.current[scrollToPassengerId];
		if (card) {
			scrollBelowProgressBar(card);
		}
		setScrollToPassengerId(null);
	}, [scrollToPassengerId]);

	const updatePassengers = React.useCallback(
		(patchFor: (passenger: PassengerDetail) => Partial<PassengerDetail> | null) => {
			const next = { ...wizardData } as any;
			passengerTypeStore.passengerTypes.forEach(passengerType => {
				const field = passengerTypeStore.getWizardDataProperty(
					passengerType.passengerTypeKey,
				) as PassengerField;
				const tickets = getPassengerTickets(next.innerWizardData, field);
				if (!tickets) {
					return;
				}
				setPassengerTickets(
					next.innerWizardData,
					field,
					tickets.map(ticket => {
						const patch = patchFor(ticket);
						return patch ? { ...ticket, ...patch } : ticket;
					}),
				);
			});
			setWizardData(next);
		},
		[wizardData, setWizardData],
	);

	/*
		Everyone starts in the vessel's default fare type, so a booker who does not care never has to
		choose. Only passengers with no fare type are touched — reopening a booking, or coming back
		to the step, must not overwrite a choice already made.

		Both legs are filled, not just the one on screen. A passenger added after the sailings were
		chosen arrives with no fare type on either leg, and the fill used to follow whichever leg the
		booker happened to open — so a party that only ever looked at the departing tab reached the
		cart with nobody assigned a fare type home.
	*/
	const defaultClassIdFor = (legData: typeof data) =>
		legData?.passengerClasses.find(x => x.isDefault && (x.remaining ?? 1) > 0)?.id;
	const departingDefaultClassId = defaultClassIdFor(legs.departing.data);
	const returningDefaultClassId = hasReturn ? defaultClassIdFor(legs.returning.data) : undefined;
	React.useEffect(() => {
		const fills: {
			defaultId: string | undefined;
			idOf: (p: PassengerDetail) => string | undefined;
			patch: (id: string) => Partial<PassengerDetail>;
		}[] = [
			{
				defaultId: departingDefaultClassId,
				idOf: p => p.passengerClassId,
				patch: id =>
					passengerClassPatch(
						'departure',
						id,
						legs.departing.data?.passengerClasses.find(x => x.id === id)?.name,
					),
			},
			{
				defaultId: returningDefaultClassId,
				idOf: p => p.returningPassengerClassId,
				patch: id =>
					passengerClassPatch(
						'return',
						id,
						legs.returning.data?.passengerClasses.find(x => x.id === id)?.name,
					),
			},
		];

		if (passengers.length === 0) {
			return;
		}

		/*
			What a passenger with no fare type is given on a leg.

			The party's own fare type where they all share one, and the vessel's default otherwise.
			A booker who moved everybody to Premium and then added a fifth traveller meant to add a
			fifth Premium traveller; handing that passenger the default put one person in a different
			fare type, silently, and split a party that was never meant to be split. Where the party
			is already split there is no "the fare type they are in", so the default is the only
			answer that does not pick a side.
		*/
		const sharedClassId = (fill: (typeof fills)[number]): string | undefined => {
			const held = passengers.map(fill.idOf).filter(Boolean) as string[];
			if (held.length === 0) {
				return undefined;
			}
			return held.every(id => id === held[0]) ? held[0] : undefined;
		};

		const patchFor = (passenger: PassengerDetail): Partial<PassengerDetail> | null => {
			let patch: Partial<PassengerDetail> | null = null;
			fills.forEach(fill => {
				if (fill.idOf(passenger)) {
					return;
				}
				const give = sharedClassId(fill) ?? fill.defaultId;
				if (!give) {
					return;
				}
				patch = { ...(patch ?? {}), ...fill.patch(give) };
			});
			return patch;
		};

		if (passengers.some(passenger => patchFor(passenger) !== null)) {
			updatePassengers(patchFor);
		}
	}, [departingDefaultClassId, returningDefaultClassId, passengers, updatePassengers]);

	/*
		Nothing is seated on arrival any more.
		
		The step used to hand everybody a seat the moment it opened, together where it could. Design 13
		replaces that with the operator's actual rule: a passenger who chooses nothing is seated at
		check-in, free, with no promise of sitting together — and choosing a seat here is the paid
		upgrade over that. Assigning up front made the fee unanswerable, because the booker was being
		charged to change something they never asked for, and it also told them a seat was theirs
		before anything had reserved it.
	*/

	if (isLoading) {
		return (
			<div className="flex justify-center p-12">
				<Spinner />
			</div>
		);
	}

	if (!data) {
		return null;
	}

	const hasClasses = data.passengerClasses.length > 0;
	const hasSeats = data.seats.length > 0;

	const updatePassenger = (passengerId: string, patch: Partial<PassengerDetail>) =>
		updatePassengers(passenger => (passenger.id === passengerId ? patch : null));

	/**
	 * What this passenger's seat choice costs, or 0 where they have not made one.
	 *
	 * <p>Zero for an unchosen seat is the whole point of design 13: a passenger seated at check-in
	 * pays nothing, and the fee is what choosing costs over that.</p>
	 */
	const seatFeeFor = (passenger: PassengerDetail): number => {
		if (!seatIdOf(passenger) || seatOutsideFareType(passenger)) {
			return 0;
		}
		return data?.passengerClasses.find(x => x.id === classIdOf(passenger))?.seatSelectionFee ?? 0;
	};

	/**
	 * This passenger's own fare in the fare type they are in.
	 *
	 * <p>Theirs, not a sample: an adult and a child in the same fare type pay different amounts, and
	 * the row names one person. Null where the trip could not price the type, in which case the money
	 * is left off the row rather than shown as zero.</p>
	 */
	const fareFor = (passenger: PassengerDetail): number | null => {
		const fare = data.passengerClasses.find(x => x.id === classIdOf(passenger))?.fares?.[passenger.key];
		return fare ?? null;
	};

	/** What this passenger costs on this leg: their fare, plus the seat if they chose one. */
	const totalFor = (passenger: PassengerDetail): number | null => {
		const fare = fareFor(passenger);
		return fare === null ? null : fare + seatFeeFor(passenger);
	};

	const seatsTakenElsewhere = (passengerId: string) =>
		passengers.filter(p => p.id !== passengerId && seatIdOf(p)).map(p => seatIdOf(p)!) as string[];

	/** Whether the seat this passenger holds belongs to another fare type — a manager's overbooking seat. */
	function seatOutsideFareType(passenger: PassengerDetail): boolean {
		const seat = data?.seats.find(x => x.id === seatIdOf(passenger));
		const classId = classIdOf(passenger);
		return !!seat && seat.passengerClassIds.length > 0 && !(!!classId && seat.passengerClassIds.includes(classId));
	}

	/**
	 * Whether this passenger's fare type has no seat left for them — every seat it sells is held by
	 * another booking or another passenger here. The state an overbooked fare type leaves its
	 * passengers in, and the one a manager is told about.
	 */
	const noSeatLeftInFareType = (passenger: PassengerDetail): boolean =>
		hasClasses &&
		!!classIdOf(passenger) &&
		selectableSeatsForClass(data.seats, classIdOf(passenger), seatsTakenElsewhere(passenger.id)).length === 0;

	/** Whether any seat at all is free for this passenger, whatever its fare type. */
	const anySeatLeft = (passenger: PassengerDetail): boolean =>
		data.seats.some(
			seat =>
				(!seat.kind || seat.kind === 'SEAT') &&
				!seat.occupied &&
				!seatsTakenElsewhere(passenger.id).includes(seat.id),
		);

	/** Whether the seat this passenger holds, if any, can be kept in the given fare type. */
	const heldSeatSuits = (passenger: PassengerDetail, passengerClassId: string): boolean => {
		const seat = data.seats.find(x => x.id === seatIdOf(passenger));
		return !seat || seat.passengerClassIds.length === 0 || seat.passengerClassIds.includes(passengerClassId);
	};

	/** Whether someone who may seat outside a fare type is being offered, or is using, such a seat. */
	const seatingOutsideFareType = (passenger: PassengerDetail): boolean =>
		store.can('SeatOutsideFareType') && (otherFareTypesFor === passenger.id || seatOutsideFareType(passenger));

	const needsSeat = (passenger: PassengerDetail): boolean => {
		if (!hasSeats) {
			return false;
		}
		if (!hasClasses) {
			return true;
		}
		const chosen = data.passengerClasses.find(c => c.id === classIdOf(passenger));
		return chosen?.allowsSeatSelection ?? false;
	};

	/**
	 * What this passenger costs, as one line beside their name.
	 *
	 * <p>The same line whether the card is open or shut. It was two layouts — a right-aligned
	 * column of figures on the shut row, and this line on the open one — so opening a passenger
	 * moved their price across the card and changed what it said.</p>
	 */
	const moneyLine = (passenger: PassengerDetail): string => {
		const note = seatMoneyNote(
			fareFor(passenger)!,
			seatFeeFor(passenger),
			needsSeat(passenger),
			!!seatIdOf(passenger),
		);

		// A note that names the fare is a breakdown of the total and is set off from it; one that
		// only qualifies it — "fare, no seat fee" — reads straight on from the figure.
		return seatFeeFor(passenger) > 0
			? `${money(totalFor(passenger)!)} · ${note}`
			: `${money(totalFor(passenger)!)} ${note}`;
	};

	// Until the booker has opened or closed something, the step picks: the first person with no fare
	// type, else simply the first. Not the first without a SEAT — nobody has one until they ask, so
	// that test is true of everyone and says nothing.
	const seatingPassenger = autoOpen
		? (passengers.find(p => !classIdOf(p)) ?? passengers[0])
		: passengers.find(p => p.id === activePassengerId);

	/** The next passenger to deal with: one still owed a fare type, else simply the next in the list. */
	const nextUnfinished = (afterId: string) => {
		const index = passengers.findIndex(p => p.id === afterId);
		const rest = passengers.slice(index + 1);
		return rest.find(p => !classIdOf(p)) ?? rest[0] ?? null;
	};

	/** Shut this passenger and open the next one, or shut it and open nobody where they were last. */
	const moveOnFrom = (passengerId: string, { scroll = false } = {}) => {
		const next = nextUnfinished(passengerId);
		setAutoOpen(false);
		setActivePassengerId(next?.id ?? null);
		setOpenDeckId(null);
		setOpenInfoClassId(null);
		setFareListOpenFor(null);
		setOtherFareTypesFor(null);
		// The next card, or the one just closed where there is nobody after it — either way the
		// booker lands on the top of what they are dealing with rather than below the list.
		if (scroll) {
			setScrollToPassengerId(next?.id ?? passengerId);
		}
	};

	/**
	 * Whether a passenger in this fare type has a free seat to pick on this leg.
	 *
	 * <p>Decides whether choosing the fare type finishes the passenger. Where the seat is free the
	 * plan is already open under the fare types, so the card stays open for it — shutting it would
	 * take the plan away the moment it became relevant — and moves on when the seat is chosen
	 * instead. A paid seat is an offer that starts shut (see `payingSeatOpenFor`), so a fare type
	 * that charges for one is finished by choosing it, the same as one with no seats.</p>
	 */
	const offersFreeSeatIn = (passengerClassId: string) => {
		if (!hasSeats) {
			return false;
		}
		const passengerClass = data.passengerClasses.find(x => x.id === passengerClassId);
		// Someone who may seat outside a fare type, whose passenger lands in one with no seat left, stays
		// on the card: it is where they are told, and where they can seat the passenger in another instead.
		if (
			store.can('SeatOutsideFareType') &&
			!!passengerClass?.allowsSeatSelection &&
			selectableSeatsForClass(data.seats, passengerClassId).length === 0 &&
			data.seats.some(seat => (!seat.kind || seat.kind === 'SEAT') && !seat.occupied)
		) {
			return true;
		}
		return (
			!!passengerClass?.allowsSeatSelection &&
			(passengerClass.seatSelectionFee ?? 0) <= 0 &&
			decksForClass(data.decks ?? [], data.seats, passengerClassId).length > 0 &&
			selectableSeatsForClass(data.seats, passengerClassId).length > 0
		);
	};

	const unclassed = hasClasses ? passengers.filter(p => !classIdOf(p)) : [];

	/** Who is still travelling on an automatic seat — the notice at the foot counts these. */
	const unseated = passengers.filter(p => needsSeat(p) && !seatIdOf(p));

	// Only the decks this passenger's class actually has seats on. Derived from the seats rather than
	// stored on the class, so a seat retagged in the admin moves the class's decks with it.
	const availableDecks =
		seatingPassenger && seatingOutsideFareType(seatingPassenger)
			? // Every deck with a seat on it, when a manager is seating outside the fare type.
				(data.decks ?? []).filter(deck => data.seats.some(seat => seat.deckId === deck.id))
			: decksForClass(data.decks ?? [], data.seats, seatingPassenger ? classIdOf(seatingPassenger) : undefined);

	// The booker's choice wins while it is still on offer; otherwise fall back to the first deck the
	// class is sold on. Without the fallback, switching a passenger to a class sold elsewhere would
	// leave the plan showing a deck they cannot sit on.
	const activeDeck = availableDecks.find(deck => deck.id === openDeckId) ?? availableDecks[0] ?? null;

	/*
		The warning `Continue` raised, over whoever is still unseated now — seating one of them takes
		them off it, and seating them all takes it away. Across both legs, because the button leaves
		the step, and grouped by leg — one line per sailing rather than the leg repeated beside every
		name, which on a return booking for five was ten names each carrying its own `(departing)`.
	*/
	const freeSeatUnchosen = unseatedFreeSeatPassengers(wizardData);
	const freeSeatUnchosenOn = (leg: 'departing' | 'returning') => freeSeatUnchosen.filter(x => x.leg === leg).length;
	const freeSeatWarning = seatWarningFor ? freeSeatUnchosen : [];
	const freeSeatWarningByLeg = (
		[
			['departing', 'Departing'],
			['returning', 'Returning'],
		] as const
	)
		.map(([value, label]) => ({
			label,
			names: freeSeatWarning.filter(x => x.leg === value).map(x => passengerName(x.passenger, passengers)),
		}))
		.filter(group => group.names.length > 0);

	/** What the passenger being seated pays for the privilege, or 0 where their class does not charge. */
	const seatingClassFee =
		data.passengerClasses.find(x => seatingPassenger && x.id === classIdOf(seatingPassenger))?.seatSelectionFee ??
		0;

	return (
		// The bottom bar is 80px and sits over the scrolling content, so the last row of the last
		// card needs room to clear it — without this the final passenger cannot be reached at all.
		<div className="seating-tab w-full pb-28 flex flex-col gap-4">
			<WizardStepHeader
				// Every other step in the wizard leads with one: contacts, groups, directions_car.
				// Without it this step's heading sat at a different weight and indent from the one
				// before it, which reads as a different kind of page rather than the next step.
				iconName="airline_seat_recline_normal"
				title={hasClasses ? 'Select fare type' : 'Choose your seats'}
				subtitle={
					hasClasses && hasSeats
						? 'One passenger at a time: pick their fare type, then their seat.'
						: hasClasses
							? 'Choose a fare type for each passenger.'
							: 'Pick a seat for each passenger.'
				}
				showCloseLink={wizardData.wizardMode === wizardModeOptions.ALTERATION}
				bookingToEditId={wizardData?.bookingToEdit ?? ''}
			/>

			{/*
				Above the leg tabs rather than at the foot of the list: `Continue` raised it, and it can
				name passengers on either leg, so it belongs above the switch between them rather than
				inside one leg's view of the step.
			*/}
			{freeSeatWarning.length > 0 && (
				// A wrapper for the ref: `Continue` scrolls to it, and MessageBox does not forward one.
				<div ref={seatWarningRef}>
					{/*
						Slim and small: it sits above the tabs and the list, and at full size it pushed the
						first passenger down by a third of the screen. One line per leg became one line for
						both — the tabs below already count each leg on its own.
					*/}
					<MessageBox
						status="warning"
						slim
						smallText
						noBottomMargin
						testId="seating-tab-free-seat-warning"
						title={
							freeSeatWarning.length === 1
								? '1 passenger has no seat chosen'
								: `${freeSeatWarning.length} passengers have no seat chosen`
						}
					>
						<p className="m-0">
							{freeSeatWarningByLeg.map((group, index) => (
								<React.Fragment key={group.label}>
									{index > 0 && ' · '}
									<b>{group.label}:</b> {group.names.join(', ')}
								</React.Fragment>
							))}
						</p>
						<p className="m-0">
							Seats are free to choose. Pick them now to sit together, or press Continue again to be
							seated at check-in.
						</p>
					</MessageBox>
				</div>
			)}

			{/*
				Two sailings, two sets of seats. Shown only on a return booking, because a one-way
				booking has nothing to switch between and a tab strip of one is furniture.
			*/}
			{showLegTabs && passengers.length > 0 && (
				<div className="seating-tab__legs">
					{(
						[
							['departing', 'Departing'],
							['returning', 'Returning'],
						] as const
					).map(([value, label]) => {
						// The date the booker chose for this leg. The seating payload knows the trip id
						// and not its timetable, and the wizard already holds the date — no reason to
						// fetch a sailing to print a day.
						const legDate =
							value === 'returning'
								? wizardData?.ticketSelectionEndDate
								: wizardData?.ticketSelectionStartDate;
						// What is LEFT, and only what is actually left: a fare type. A seat is optional —
						// a passenger who chooses none is seated at check-in, which is a finished state
						// and not an outstanding one. Counting unseated passengers made a leg the booker
						// had completed report "3 to do" and sent them back to do nothing.
						const outstanding = passengers.filter(
							p => !(value === 'returning' ? p.returningPassengerClassId : p.passengerClassId),
						).length;

						return (
							<button
								key={value}
								type="button"
								onClick={() => {
									setLeg(value);
									// Nobody is opened for the booker on arrival at the other leg.
									// Switching legs from the tabs is a look, not a decision — the
									// step used to open the first passenger's card and scroll a deck
									// plan under someone who only wanted to check what they had
									// already done. What was open is cleared by the effect on `leg`.
									setAutoOpen(false);
								}}
								className={`seating-tab__leg ${activeLeg === value ? 'seating-tab__leg--on' : ''}`}
							>
								<span className="seating-tab__leg-name">
									{label}
									{activeLeg !== value && outstanding > 0 && (
										<span className="seating-tab__todo">{outstanding} to do</span>
									)}
									{/*
										On both tabs, the open one included: the same count the Continue
										warning is about, so the booker can see which leg it means
										before pressing it. Free seats only — passing over a paid
										seat is declining an upsell, not leaving something out.
									*/}
									{freeSeatUnchosenOn(value) > 0 && (
										<span
											className="seating-tab__todo seating-tab__todo--seats"
											data-test-id={`seating-tab-${value}-free-seats-unchosen`}
										>
											{freeSeatUnchosenOn(value)} without a seat
										</span>
									)}
								</span>
								{legDate && (
									<span className="seating-tab__leg-date">{moment(legDate).format('ddd D MMM')}</span>
								)}
							</button>
						);
					})}
				</div>
			)}

			{passengers.length === 0 && (
				<div className="text-sm text-gray-500">
					<FormattedMessage
						id="seating-tab__no-passengers__label"
						defaultMessage="Add a passenger before choosing seating."
					/>
				</div>
			)}

			{passengers.length > 0 && (
				<section className="seating-tab__list">
					{passengers.map(passenger => {
						const chosen = data.passengerClasses.find(c => c.id === classIdOf(passenger));
						const expanded = seatingPassenger?.id === passenger.id;

						return (
							<div
								key={passenger.id}
								ref={element => {
									cardRefs.current[passenger.id] = element;
								}}
								className={expanded ? 'seating-tab__open seating-tab__card' : 'seating-tab__card'}
							>
								<button
									type="button"
									className={expanded ? 'seating-tab__open-head' : 'seating-tab__row'}
									onClick={() => {
										setAutoOpen(false);
										setActivePassengerId(expanded ? null : passenger.id);
										// The open deck was chosen for whoever was open before, and the
										// next passenger may be in a class that is not sold on it.
										setOpenDeckId(null);
										// The panel lives inside this list, so collapsing it would
										// otherwise leave the panel open against a list nobody can see.
										setOpenInfoClassId(null);
										setFareListOpenFor(null);
										setOtherFareTypesFor(null);
									}}
								>
									{/*
										The passenger and what they cost, open or shut. The type was
										printed under every name ("Adult" under "Adult 1"), which is the
										name again with the number taken off.
									*/}
									<span className={expanded ? 'seating-tab__open-who' : 'seating-tab__who'}>
										<span className={expanded ? 'seating-tab__open-name' : undefined}>
											{passengerName(passenger, passengers)}
										</span>
										{totalFor(passenger) !== null && (
											<span className="seating-tab__price-note">{moneyLine(passenger)}</span>
										)}
									</span>

									<span className="seating-tab__marks">
										{/*
											The fare type in its own colour rather than the brand's. On a
											list of four passengers the brand pill said only "something is
											chosen"; the colour says WHICH, and matches the deck plan.
										*/}
										<span
											className={`seating-tab__fare ${chosen ? '' : 'seating-tab__fare--none'}`}
											style={chosen ? chosenPillStyle(chosen, data.passengerClasses) : undefined}
										>
											{chosen && (
												<i
													style={{ background: chosenColour(chosen, data.passengerClasses) }}
												/>
											)}
											{chosen ? chosen.name : 'Select fare type'}
										</span>
										{/*
											The seat sits beside the class rather than only in the diagram,
											so every passenger's seat can be read off one list — checking
											three seats meant clicking through three passengers otherwise.
										*/}
										{/*
											What this passenger has, in the operator's own terms. No seat
											is not an omission — it is the free option, and saying "No
											seat" made it read as something left undone.
										*/}
										{needsSeat(passenger) && seatLabelOf(passenger) && (
											<span className="seating-tab__seat">Seat {seatLabelOf(passenger)}</span>
										)}
										{needsSeat(passenger) && !seatLabelOf(passenger) && (
											<span className="seating-tab__auto">
												<span className="seating-tab__badge">A</span>
												Seat assigned at check-in
											</span>
										)}
									</span>

									<span className="seating-tab__chevron">
										<Icon name={expanded ? 'expand_less' : 'chevron_right'} useMaterialIcons />
									</span>
								</button>

								{expanded && (
									<div className="seating-tab__open-body">
										{hasClasses &&
											data.passengerClasses
												.filter(
													passengerClass =>
														!classIdOf(passenger) ||
														fareListOpenFor === passenger.id ||
														passengerClass.id === classIdOf(passenger),
												)
												.map(passengerClass => {
													const isChosen = passengerClass.id === classIdOf(passenger);
													const fare = passengerClass.fares?.[passenger.key] ?? null;
													const current = classIdOf(passenger)
														? (data.passengerClasses.find(
																x => x.id === classIdOf(passenger),
															)?.fares?.[passenger.key] ?? null)
														: null;

													return (
														<FareTypeOption
															key={passengerClass.id}
															passengerClass={passengerClass}
															chosen={isChosen}
															allowFull={store.can('OverbookInBookingWizard')}
															fare={fare}
															delta={
																fare !== null && current !== null && !isChosen
																	? fare - current
																	: null
															}
															onMoreInfo={
																hasMoreInfo(passengerClass)
																	? () =>
																			setOpenInfoClassId(current2 =>
																				current2 === passengerClass.id
																					? null
																					: passengerClass.id,
																			)
																	: undefined
															}
															onChoose={() => {
																// The new fare type may not be sold on the deck that
																// is open, and the plan should land on one it is.
																setOpenDeckId(null);
																// The fare type alone. Tapping the fare type a
																// passenger is already in threw their seat away, and
																// on a change the seat may well still be sold in the
																// new one — only this sailing's seating can say, and
																// the reconciliation in `useSeatingTab` asks it.
																updatePassenger(passenger.id, {
																	...classPatch(passengerClass.id),
																	// Except a seat the new fare type cannot have. The
																	// reconciliation drops it for everyone but a
																	// manager, who may hold one outside their fare
																	// type — so for them it has to go here, or a seat
																	// kept on purpose and one merely left behind by a
																	// change of fare type would look the same.
																	...(heldSeatSuits(passenger, passengerClass.id)
																		? {}
																		: seatPatch(undefined, undefined)),
																});
																// Folds back to the one they chose.
																setFareListOpenFor(null);
																// Chosen, so on to the next passenger — unless
																// this fare type has a free seat to pick, which is
																// the rest of this passenger's card.
																if (!offersFreeSeatIn(passengerClass.id)) {
																	moveOnFrom(passenger.id);
																}
															}}
														/>
													);
												})}

										{hasClasses &&
											!!classIdOf(passenger) &&
											fareListOpenFor !== passenger.id &&
											data.passengerClasses.length > 1 && (
												<button
													type="button"
													className="seating-tab__change-fare"
													data-test-id="seating-tab-change-fare-type"
													onClick={() => setFareListOpenFor(passenger.id)}
												>
													Change fare type
												</button>
											)}

										{/*
											Mounted after the list rather than inside a row: the rows are
											buttons, and a panel nested in one would make every click on it
											choose that class.
										*/}
										{openInfoClassId && (
											<ClassMoreInfo
												passengerClass={
													data.passengerClasses.find(x => x.id === openInfoClassId)!
												}
												onClose={() => setOpenInfoClassId(null)}
											/>
										)}

										{/*
											This passenger's seat, in this passenger's card. It was a
											second section below the list, which meant the notice about
											seat fees, the line naming who was being seated, and the deck
											itself all sat apart from the person they were about to
											change — and the booker had to keep track of which of the
											three names above applied to the plan below.
										*/}
										{/*
											An overbooked fare type, told to the one person able to fix it. Above the
											seat block rather than in it: that block is drawn from the decks the fare
											type has a free seat on, and here it has none.
										*/}
										{needsSeat(passenger) &&
											store.can('SeatOutsideFareType') &&
											!seatIdOf(passenger) &&
											otherFareTypesFor !== passenger.id &&
											noSeatLeftInFareType(passenger) &&
											anySeatLeft(passenger) && (
												<MessageBox
													status="warning"
													slim
													smallText
													noBottomMargin
													testId="seating-tab-no-seat-in-fare-type"
													title={`No seats left in ${
														data.passengerClasses.find(x => x.id === classIdOf(passenger))
															?.name ?? 'this fare type'
													}`}
													actions={
														<button
															type="button"
															className="seating-tab__seat-offer"
															onClick={() => setOtherFareTypesFor(passenger.id)}
														>
															Show seats in other fare types
														</button>
													}
												>
													<p className="m-0">
														This fare type is overbooked. You can seat{' '}
														{passengerName(passenger, passengers)} in another fare
														type&apos;s seat, free of charge — they keep their own fare type
														and fare.
													</p>
												</MessageBox>
											)}

										{needsSeat(passenger) && store.can('SeatOutsideFareType') && seatOutsideFareType(passenger) && (
											<MessageBox
												status="info"
												slim
												smallText
												noBottomMargin
												testId="seating-tab-seat-outside-fare-type"
											>
												<p className="m-0">
													{seatLabelOf(passenger)
														? `Seat ${seatLabelOf(passenger)}`
														: 'This seat'}{' '}
													is in another fare type, so there is no seat fee.
												</p>
											</MessageBox>
										)}

										{needsSeat(passenger) && activeDeck && (
											<div className="mt-3 pt-3 border-t border-gray-200 flex flex-col gap-3">
												{/*
													Automatic assignment is the standing state, so it is
													stated before the plan rather than as an absence after
													it. A booker who reads this and does nothing has made
													a valid, free choice — which is the thing the old
													"no seat" chip never managed to say.
												*/}
												{!seatIdOf(passenger) && (
													<div className="seating-tab__note">
														<span className="seating-tab__note-mark">A</span>
														<span>
															<span className="seating-tab__note-title">
																A seat will be assigned at check-in
															</span>
															<span>
																No charge, but you may not be seated with the rest of
																your party.
																{seatingClassFee > 0 && (
																	<>
																		{' '}
																		Choosing a seat in{' '}
																		{
																			data.passengerClasses.find(
																				x => x.id === classIdOf(passenger),
																			)?.name
																		}{' '}
																		costs <b>{money(seatingClassFee)}</b>.
																	</>
																)}
															</span>
														</span>
													</div>
												)}

												{/*
													The offer, where the seat costs something. Shut until
													it is taken up: see `payingSeatOpenFor`.
												*/}
												{seatingClassFee > 0 &&
													!seatingOutsideFareType(passenger) &&
													!holdsChosenSeat(passenger) &&
													payingSeatOpenFor !== passenger.id && (
														<button
															type="button"
															className="seating-tab__seat-offer"
															onClick={() => setPayingSeatOpenFor(passenger.id)}
														>
															{`Choose a seat for ${money(seatingClassFee)}`}
														</button>
													)}

												{(seatingClassFee === 0 ||
													seatingOutsideFareType(passenger) ||
													holdsChosenSeat(passenger) ||
													payingSeatOpenFor === passenger.id) && (
													<>
														{/*
													The plan's own heading, and what tapping it will do to
													this passenger's price — said here because this is the
													last moment before the money changes.
												*/}
														<div className="seating-tab__plan-head">
															<span className="seating-tab__plan-title">
																{seatIdOf(passenger)
																	? 'Change seat'
																	: 'Choose a seat instead'}
															</span>
															<span className="seating-tab__plan-hint">
																Tap any available seat
																{seatingClassFee > 0 &&
																	!seatingOutsideFareType(passenger) &&
																	!holdsChosenSeat(passenger) && (
																		<> · {money(seatingClassFee)}</>
																	)}
																{seatingClassFee > 0 &&
																	!seatingOutsideFareType(passenger) &&
																	fareFor(passenger) !== null &&
																	!seatIdOf(passenger) && (
																		<>
																			, total becomes{' '}
																			{money(
																				fareFor(passenger)! + seatingClassFee,
																			)}
																		</>
																	)}
															</span>
														</div>

														{/*
													What the seat they hold has already cost, not what
													another one would.

													This read "changing it costs $3.00" from the moment the
													first seat was picked, which is wrong twice over. The fee
													buys the choice, so it was charged when they chose — and
													because it is charged per seat held rather than per
													change, moving to a different seat leaves the count, and
													therefore the charge, exactly where it was.
												*/}
														{seatingClassFee > 0 && seatIdOf(passenger) && (
															<div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-800">
																<b>
																	{seatLabelOf(passenger)
																		? `Seat ${seatLabelOf(passenger)} was chosen for ${passengerName(passenger, passengers)}`
																		: `A seat was chosen for ${passengerName(passenger, passengers)}`}
																</b>
																{' — '}
																its <b>{money(seatingClassFee)}</b> seat fee is
																included. Moving to another seat costs nothing more.
															</div>
														)}

														{availableDecks.length > 1 && (
															<div className="flex flex-wrap gap-2">
																{availableDecks.map(deck => (
																	<button
																		key={deck.id}
																		type="button"
																		onClick={() => setOpenDeckId(deck.id)}
																		className={`px-3 py-1 rounded-full text-xs border ${
																			deck.id === activeDeck?.id
																				? 'border-[var(--colour-brand-primary)] text-[var(--colour-brand-primary)]'
																				: 'border-gray-300 text-gray-600'
																		}`}
																	>
																		{deck.name}
																	</button>
																))}
															</div>
														)}

														{activeDeck?.seatMapImageId && (
															<img
																src={`${IMAGE_API_URL}${activeDeck.seatMapImageId}`}
																alt={`${activeDeck.name} plan`}
																className="max-w-full max-h-[360px] self-start rounded border border-gray-200"
															/>
														)}

														<SeatingDeck
															seats={data.seats.filter(
																seat => seat.deckId === activeDeck.id,
															)}
															passengerClasses={data.passengerClasses}
															activeClassId={classIdOf(passenger)}
															allowOtherFareTypes={seatingOutsideFareType(passenger)}
															takenOnThisBooking={seatsTakenElsewhere(passenger.id)}
															features={parseDeckFeatures(activeDeck.deckFeatures)}
															selectedSeatId={seatIdOf(passenger)}
															onSelect={(seat: SeatingTabSeat) => {
																const deselecting = seat.id === seatIdOf(passenger);
																updatePassenger(
																	passenger.id,
																	deselecting
																		? seatPatch(undefined, undefined)
																		: seatPatch(
																				seat.id,
																				deckSeatLabel(activeDeck.name, seat),
																			),
																);
																// A seat chosen finishes the passenger. Tapping
																// their own seat to give it up does not — they are
																// most likely about to pick another.
																if (!deselecting) {
																	moveOnFrom(passenger.id, { scroll: true });
																}
															}}
														/>
													</>
												)}
											</div>
										)}

										{/*
											Forward, one passenger at a time. Without it the booker has to
											work out for themselves that the next person is another click
											up the list, which is the step where a party of four loses one.
										*/}
										{(() => {
											const next = nextUnfinished(passenger.id);
											return (
												<div className="mt-3 flex justify-end">
													<button
														type="button"
														className="px-3 py-1.5 rounded-md text-sm bg-[var(--colour-brand-primary)] text-white"
														onClick={() => {
															// Even with nobody next: Done means shut this
															// one, and the card folds back to its summary
															// line.
															moveOnFrom(passenger.id);
														}}
													>
														{next ? `Next — ${passengerName(next, passengers)}` : 'Done'}
													</button>
												</div>
											);
										})()}
									</div>
								)}
							</div>
						);
					})}
				</section>
			)}

			{/*
				What the booker has not done, and what it will cost them if they leave it. Both belong
				at the foot of the list rather than inside a card: they are about the party, and a
				notice inside one passenger's card is a notice three of them never see.
			*/}
			{hasSeats && unseated.length > 0 && freeSeatWarning.length === 0 && (
				<div className="seating-tab__note">
					<span className="seating-tab__note-mark">i</span>
					<span>
						{unseated.length === 1
							? `${passengerName(unseated[0], passengers)} has no seat chosen.`
							: `${unseated.length} passengers have no seat chosen.`}{' '}
						Seats are assigned automatically at check-in at no charge — travelling together isn&apos;t
						guaranteed. Choose seats now to sit together.
					</span>
				</div>
			)}

			{/*
				No total and no onward button here. The cart carries the money for the whole booking
				and said it again per leg, which is two figures for one booking; and the step's own
				"Continue to return seating" sat under the wizard's own Continue doing what the
				booker had already reached for. The leg now moves on that button — see the Seats
				step's `validateStep` in `PassengerOnlyWizardSteps`.
			*/}

			{hasSeats && unclassed.length > 0 && (
				<p className="text-sm text-gray-500">
					{unclassed.map(x => passengerName(x, passengers)).join(', ')}
					{unclassed.length === 1 ? ' still needs' : ' still need'} a fare type before choosing a seat.
				</p>
			)}
		</div>
	);
}

/**
 * What to call a passenger who has not been named yet.
 *
 * <p>Numbered within their type — "Adult 1", "Adult 2" — because on a booking for three adults the
 * bare type name is the same three times, and the step asks the booker to choose a class and a seat
 * for each of them. Three identical labels is three rows nobody can tell apart.</p>
 *
 * <p>The number is only ever a position in the list. A booking for one adult says "Adult", because
 * "Adult 1" implies a second.</p>
 */
function passengerName(passenger: PassengerDetail, all: PassengerDetail[] = []): string {
	// Only where the operator collects them. With passenger names off, the only name on the booking
	// is the account holder's, which gets copied onto the first traveller — so the row said
	// "Customer Fourlastname" for someone the booker never named, while everyone else stayed
	// "Adult 2". Numbered type names for all of them is the honest reading of that booking.
	const namesCollected =
		appConfigStore.passengerInfoEnabled &&
		(appConfigStore.passengerFirstNameEnabled || appConfigStore.passengerLastNameEnabled);
	const name = namesCollected ? [passenger.firstName, passenger.lastName].filter(Boolean).join(' ').trim() : '';
	if (name.length > 0) {
		return name;
	}

	const typeName = passengerTypeStore.getSingularName(passenger.key as PassengerTypeKey);
	const sameType = all.filter(x => x.key === passenger.key);
	if (sameType.length < 2) {
		return typeName;
	}

	return `${typeName} ${sameType.findIndex(x => x.id === passenger.id) + 1}`;
}

/** Every passenger on the booking, across all eight type slots, in display order. */
function collectPassengers(wizardData: any): PassengerDetail[] {
	const out: PassengerDetail[] = [];
	passengerTypeStore.passengerTypes.forEach(passengerType => {
		const field = passengerTypeStore.getWizardDataProperty(passengerType.passengerTypeKey) as PassengerField;
		const tickets = getPassengerTickets(wizardData.innerWizardData, field);
		if (tickets) {
			out.push(...tickets);
		}
	});
	return out;
}

export type { SeatingTabClass, SeatingTabSeat };

/**
 * Scroll an element to the top of the step, clear of the wizard's progress bar.
 *
 * <p>The bar is fixed over the top of the scrolling step, so an element scrolled to the top of it —
 * or centred, when it sits near the top — landed underneath: a passenger card with its name hidden,
 * the Continue warning off the screen entirely. Measured rather than hard-coded, because the bar is a
 * different height on a phone, and only when it is actually fixed; a bar in the flow covers nothing.</p>
 */
function scrollBelowProgressBar(element: HTMLElement) {
	const bar = document.querySelector('.booking-wizard-progress-bar-container');
	const barHeight = bar && getComputedStyle(bar).position === 'fixed' ? bar.getBoundingClientRect().height : 0;
	// eslint-disable-next-line no-param-reassign
	element.style.scrollMarginTop = `${barHeight + 16}px`;
	element.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * Whether a class has anything behind its ⓘ.
 *
 * <p>Drawn only when it does: an information marker that opens an empty panel is worse than no
 * marker, because the booker has to click it to find that out.</p>
 */
function hasMoreInfo(passengerClass: SeatingTabClass): boolean {
	return (
		(passengerClass.attributes ?? []).some(x => x.showInMoreInfo) ||
		describeClassPoints(passengerClass.description).length > 0
	);
}

/** The fare type's colour, resolved the same way the deck plan resolves it. */
const chosenColour = (passengerClass: SeatingTabClass, all: SeatingTabClass[]): string =>
	classColour(
		all.findIndex(x => x.id === passengerClass.id),
		passengerClass.colour,
	);

/**
 * A pill in the fare type's own colour, tinted rather than filled.
 *
 * <p>`color-mix` rather than a second configured colour: an operator sets one colour per fare type,
 * and a readable tint of it is arithmetic, not another decision to make.</p>
 */
const chosenPillStyle = (passengerClass: SeatingTabClass, all: SeatingTabClass[]): React.CSSProperties => {
	const colour = chosenColour(passengerClass, all);
	return {
		background: `color-mix(in srgb, ${colour} 14%, white)`,
		color: `color-mix(in srgb, ${colour} 75%, black)`,
	};
};

/**
 * The line under a passenger's total, saying what the seat did to it.
 *
 * <p>Three different facts, and the difference between them is the whole fee conversation. A fare
 * type that includes seat choice has nothing to add; one that charges shows the sum; a passenger who
 * chose nothing is told, in the same breath as their price, that they were not charged for it.</p>
 */
const seatMoneyNote = (fare: number, seatFee: number, seatable: boolean, hasSeat: boolean): string => {
	if (!seatable) {
		return 'fare';
	}
	if (seatFee > 0) {
		return `${money(fare)} fare + ${money(seatFee)} seat`;
	}
	// On THIS leg. A booker who seated the outbound trip and not the return must not be told the
	// return seat is included.
	return hasSeat ? 'fare, seat included' : 'fare, no seat fee';
};
